export const activeDirectoryConfig = {
  name: "active-directory",
  displayName: "Service Accounts", // Changed to Service Accounts to match screenshot title
  description: "Active Directory security monitoring and identity management",
  icon: "/inte/ActiveDirectory.png",
  category: "Service Accounts",
  viewType: "table",
  integrationTableColumns: [
    { key: "name", label: "Domain Name", cell: "text" },
    { key: "domain_controller", label: "Domain Controller", cell: "text" },
    { key: "stats.users_count", label: "Users", cell: "number" },
    {
      key: "risk_level",
      label: "Risk Level",
      cell: "status_badge",
      statusMap: {
        high: {
          label: "High",
          color: "red",
          bg: "bg-red-100",
          text: "text-red-800",
        },
        medium: {
          label: "Medium",
          color: "orange",
          bg: "bg-orange-100",
          text: "text-orange-800",
        },
        low: {
          label: "Low",
          color: "blue",
          bg: "bg-blue-100",
          text: "text-blue-800",
        },
      },
    },
    { key: "predictability", label: "Predictability", cell: "text" },
    { key: "status", label: "Status", cell: "status_badge" },
    { key: "created_at", label: "Connected On", cell: "date" },
  ],

  // API Endpoints
  endpoints: {
    list: {
      url: "api/active-directory/domains",
      method: "POST",
      useFilters: true,
      filterPreset: "onboardedDomains",
    },
    details: "api/active-directory/domains/{id}",
    findings: {
      url: "api/active-directory/domains/{id}/findings",
      method: "GET",
      useFilters: true,
    },
    stats: {
      url: "api/findings/cards",
      params: { type: "iam", provider: "active_directory", hours: "24" },
    },
    create: "api/active-directory/domains",
    update: "api/active-directory/domains/{id}",
    delete: "api/active-directory/domains/{id}",
    update: "api/active-directory/domains/{id}",
    delete: "api/active-directory/domains/{id}",
  },

  // Mock Data for Integration List (Domains)
  mockIntegrationsData: [
    {
      id: "mock-domain-1",
      integration_id: "mock-domain-1",
      name: "ad.acaws.stackguard.io",
      domain_controller: "dc01.ad.acaws.stackguard.io",
      stats: {
        users_count: 15420,
      },
      status: "active",
      created_at: "2024-05-20T10:00:00Z",
      risk_level: "low",
      predictability: "High",
      totalFindings: 35,
      verifiedFindings: 28,
      type: "active-directory",
    },
    {
      id: "mock-domain-2",
      integration_id: "mock-domain-2",
      name: "prod.corp.local",
      domain_controller: "dc-prod-01.corp.local",
      stats: {
        users_count: 8530,
      },
      status: "active",
      created_at: "2024-06-15T08:30:00Z",
      risk_level: "high",
      predictability: "Medium",
      totalFindings: 38,
      verifiedFindings: 30,
      type: "active-directory",
    },
    {
      id: "mock-domain-3",
      integration_id: "mock-domain-3",
      name: "dev.lab.internal",
      domain_controller: "dc-dev-01.lab.internal",
      stats: {
        users_count: 2100,
      },
      status: "active",
      created_at: "2024-08-01T14:15:00Z",
      risk_level: "medium",
      predictability: "Low",
      totalFindings: 32,
      verifiedFindings: 24,
      type: "active-directory",
    },
  ],

  // Mock Data Configuration & finding columns
  mockStatsData: {
    service_accounts: 6,
    highly_privileged: 3,
    interactive_login: 1,
    broadly_used: 1,
    protected_accounts: {
      protected: 4,
      unprotected: 2,
    },
  },

  mockFindingsData: [
    {
      id: "1",
      account_email: "bind@ad.acaws.silverfort.io",
      source_count: 1,
      destination_count: 1,
      auth_count: 5112,
      risk_level: "Low",
      predictability: "High",
      is_protected: true,
      verified: true,
    },
    {
      id: "2",
      account_email: "svc_drbackup@ad.acaws.silverfort.io",
      source_count: 7,
      destination_count: 4,
      auth_count: 101130,
      risk_level: "Medium",
      predictability: "High",
      is_protected: true,
      verified: true,
    },
    {
      id: "3",
      account_email: "intranet-svc@ad.acaws.silverfort.io",
      source_count: 6,
      destination_count: 4,
      auth_count: 192,
      risk_level: "Low",
      predictability: "Low",
      is_protected: true,
      verified: true,
    },
    {
      id: "4",
      account_email: "MSOL_14027b699981",
      source_count: 1,
      destination_count: 0,
      auth_count: 0,
      risk_level: "High",
      predictability: "Low",
      is_protected: false,
      verified: false,
    },
    {
      id: "5",
      account_email: "svc_app01@ad.acaws.silverfort.io",
      source_count: 3,
      destination_count: 3,
      auth_count: 41,
      risk_level: "Low",
      predictability: "Low",
      is_protected: false,
      verified: true,
    },
    {
      id: "6",
      account_email: "svc_file_manager@ad.acaws.silverfort.io",
      source_count: 6,
      destination_count: 7,
      auth_count: 1688,
      risk_level: "Low",
      predictability: "Low",
      is_protected: true,
      verified: true,
    },
  ],

  // Card Configuration
  cardFields: [
    {
      key: "name",
      label: "Domain Name",
      type: "title",
      truncate: true,
    },
    {
      key: "domain_controller",
      label: "Domain Controller",
      type: "subtitle",
    },
    {
      key: "stats.users_count",
      label: "Users",
      type: "number",
    },
    {
      key: "created_at",
      label: "Connected On",
      type: "date",
      icon: "Calendar",
    },
  ],

  // Table Columns for Findings
  findingColumns: [
    {
      accessorKey: "account_email",
      header: "SERVICE ACCOUNT",
      cell: "account_with_icon",
      width: "w-[30%]",
    },
    {
      accessorKey: "source_count",
      header: "SOURCE",
      cell: "count_label",
      suffix: "Source",
      pluralSuffix: "Sources",
      width: "w-[12%]",
      align: "center",
    },
    {
      accessorKey: "destination_count",
      header: "DESTINATION",
      cell: "count_label",
      suffix: "Destination",
      pluralSuffix: "Destinations",
      width: "w-[12%]",
      align: "center",
    },
    {
      accessorKey: "auth_count",
      header: "AUTHENTICATIONS",
      cell: "number",
      width: "w-[12%]",
    },
    {
      accessorKey: "risk_level",
      header: "RISK",
      cell: "risk_badge",
      width: "w-[10%]",
    },
    {
      accessorKey: "predictability",
      header: "PREDICTABILITY",
      cell: "text",
      width: "w-[12%]",
    },
  ],

  // Stats Configuration
  statsCards: [
    {
      key: "service_accounts",
      title: "Service Accounts",
      dataPath: "service_accounts",
      icon: "UserCog",
    },
    {
      key: "highly_privileged",
      title: "Highly Privileged",
      dataPath: "highly_privileged",
      icon: "ShieldAlert",
    },
    {
      key: "interactive_login",
      title: "Interactive login",
      dataPath: "interactive_login",
      icon: "UserCheck",
    },
    {
      key: "protected_accounts",
      title: "Protected Accounts",
      dataPath: "protected_accounts",
      type: "progress_bar",
      labels: ["Protected", "Unprotected Accounts"],
      colors: ["blue", "gray"],
    },
  ],

  // Action Menu Items
  actions: [
    {
      key: "ignore_finding",
      label: "Ignore Finding",
      icon: "X",
      action: "api",
      endpoint: "api/active-directory/findings/ignore",
      method: "PUT",
    },
  ],

  // Filter Configuration
  filterConfig: {
    serverSideFiltering: false, // Use client side for mock data
    supportedFilters: ["risk_level", "predictability"],
    defaultFilter: "all",
    filterLabels: {
      High: "High",
      Medium: "Medium",
      Low: "Low",
    },
  },

  // Integration-specific features
  features: {
    hasInsights: true,
    hasBlastRadius: false,
    supportsAutoFix: false,
    supportsVaultRemediation: false,
    supportsTicketing: false,
    allowEmptyFindingsClick: true,
    showCheckboxes: false,
    showActions: false,
  },
};
